import React, { useState, useEffect } from 'react';

interface OffsetInputDialogProps {
  isOpen: boolean;
  onConfirm: (offset: { x: number; y: number; z: number; copies: number }) => void;
  onCancel: () => void;
  selectedCount: {
    nodes: number;
    beams: number;
  };
}

export const OffsetInputDialog: React.FC<OffsetInputDialogProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  selectedCount
}) => {
  const [xOffset, setXOffset] = useState('0');
  const [yOffset, setYOffset] = useState('0');
  const [zOffset, setZOffset] = useState('0');
  const [copies, setCopies] = useState('1');
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setXOffset('0');
      setYOffset('0');
      setZOffset('0');
      setCopies('1');
      setError(null);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    // Validate inputs
    const x = parseFloat(xOffset);
    const y = parseFloat(yOffset);
    const z = parseFloat(zOffset);
    const numCopies = parseInt(copies, 10);

    if (isNaN(x) || isNaN(y) || isNaN(z) || isNaN(numCopies)) {
      setError('Please enter valid numbers for all offsets');
      return;
    }

    if (numCopies < 1) {
      setError('Number of copies must be at least 1');
      return;
    }

    // Call onConfirm with validated offset values and number of copies
    onConfirm({ x, y, z, copies: numCopies });
  };

  const handleCancel = () => {
    onCancel();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 shadow-xl">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Paste with Offset</h2>
        
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Pasting {selectedCount.nodes} nodes and {selectedCount.beams} beams
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              X Offset
            </label>
            <input
              type="number"
              value={xOffset}
              onChange={(e) => setXOffset(e.target.value)}
              className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
              step="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Y Offset
            </label>
            <input
              type="number"
              value={yOffset}
              onChange={(e) => setYOffset(e.target.value)}
              className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
              step="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Z Offset
            </label>
            <input
              type="number"
              value={zOffset}
              onChange={(e) => setZOffset(e.target.value)}
              className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
              step="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Number of Copies
            </label>
            <input
              type="number"
              value={copies}
              onChange={(e) => setCopies(e.target.value)}
              className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="1"
              min="1"
              step="1"
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm mt-2">
              {error}
            </div>
          )}

          <div className="flex justify-between space-x-2 mt-6">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-md border border-gray-300 dark:border-gray-600"
            >
              Cancel
            </button>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setXOffset('0');
                  setYOffset('0');
                  setZOffset('0');
                  setCopies('1');
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-md border border-gray-300 dark:border-gray-600"
                title="Reset all values"
              >
                Reset
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Paste
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OffsetInputDialog;